import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import PageTitle from "../../components/PageTitle/PageTitle";
import StudentChargeMasterService from "./Locality/Service/studentChargeMasterService";

export default function StudentChargeMaster() {
  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isSuperAdmin = String(userDetails?.role || "").toUpperCase() === "SUPERADMIN";

  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const validationSchema = useMemo(
    () =>
      Yup.object({
        amountPerStudent: Yup.number()
          .typeError("Amount is required")
          .positive("Amount must be > 0")
          .required("Amount is required"),
        status: Yup.string().oneOf(["Active", "Inactive"]).required("Status is required"),
      }),
    [],
  );

  const formik = useFormik({
    initialValues: { _id: "", amountPerStudent: "", status: "Active" },
    validationSchema,
    onSubmit: async (values) => {
      const amt = Number(values.amountPerStudent);
      if (!Number.isFinite(amt) || amt <= 0) return;
      setSaving(true);
      try {
        await StudentChargeMasterService.upsert({
          _id: values._id || undefined,
          amountPerStudent: amt,
          status: values.status,
        });
        setOpen(false);
        await load();
      } finally {
        setSaving(false);
      }
    },
  });

  const load = async () => {
    const res = await StudentChargeMasterService.list();
    const data = res?.data || res;
    setItems(Array.isArray(data?.rows) ? data.rows : Array.isArray(data?.items) ? data.items : []);
  };

  useEffect(() => {
    if (!isSuperAdmin) return;
    load().catch(() => {});
  }, [isSuperAdmin]);

  const openNew = () => {
    formik.resetForm();
    formik.setValues({ _id: "", amountPerStudent: "", status: "Active" });
    setOpen(true);
  };

  const openEdit = (row) => {
    formik.setValues({
      _id: row?._id || "",
      amountPerStudent: String(row?.amountPerStudent ?? ""),
      status: String(row?.status || "Inactive"),
    });
    setOpen(true);
  };

  const onDelete = async (row) => {
    if (!row?._id) return;
    // eslint-disable-next-line no-alert
    const ok = window.confirm("Delete this master row?");
    if (!ok) return;
    await StudentChargeMasterService.delete(row._id);
    await load();
  };

  if (!isSuperAdmin) {
    return (
      <Box p={3}>
        <Typography>You don&apos;t have access to this page.</Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <PageTitle
        title="Student Charge Master"
        button={
          <Button variant="contained" color="primary" onClick={openNew} style={{ textTransform: "none" }}>
            Add Master
          </Button>
        }
      />

      <Card style={{ marginTop: 14, maxWidth: 900 }}>
        <CardContent>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Amount (per student)</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Edit</TableCell>
                <TableCell align="right">Delete</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <TableRow key={row._id} style={row.status === "Active" ? { background: "rgba(34,197,94,0.06)" } : null}>
                  <TableCell>₹{Number(row.amountPerStudent || 0).toLocaleString()}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "-"}</TableCell>
                  <TableCell align="right">
                    <EditIcon style={{ cursor: "pointer" }} onClick={() => openEdit(row)} />
                  </TableCell>
                  <TableCell align="right">
                    <DeleteIcon style={{ cursor: "pointer" }} onClick={() => onDelete(row)} />
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} style={{ color: "#6b7280" }}>
                    No master rows yet. Add one and keep it Active.
                  </TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
          <Typography variant="body2" style={{ color: "#6b7280", marginTop: 10 }}>
            Note: Amount used for Upgrade Plan = (Number of students) × (Active amount per student).
          </Typography>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={() => (saving ? null : setOpen(false))} fullWidth maxWidth="xs">
        <DialogTitle>{formik.values._id ? "Edit Master" : "Add Master"}</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gridGap={12}>
            <TextField
              variant="outlined"
              label="Amount per student"
              name="amountPerStudent"
              type="text"
              value={formik.values.amountPerStudent}
              onChange={(e) => {
                const raw = e?.target?.value ?? "";
                const digitsOnly = String(raw).replace(/[^\d]/g, "");
                formik.setFieldValue("amountPerStudent", digitsOnly);
              }}
              onBlur={formik.handleBlur}
              error={formik.touched.amountPerStudent && Boolean(formik.errors.amountPerStudent)}
              helperText={formik.touched.amountPerStudent && formik.errors.amountPerStudent}
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              fullWidth
            />
            <FormControl variant="outlined">
              <InputLabel id="scm-status-label">Status</InputLabel>
              <Select
                labelId="scm-status-label"
                value={formik.values.status}
                onChange={(e) => formik.setFieldValue("status", String(e?.target?.value ?? "Active"))}
                onBlur={formik.handleBlur}
                label="Status"
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            color="primary"
            variant="contained"
            onClick={formik.handleSubmit}
            disabled={saving || !formik.isValid || !formik.values.amountPerStudent || Number(formik.values.amountPerStudent) <= 0}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

